// Typed patient identifiers whose offline write could not land yet, held per
// session id until the sync-queue mirror names a local row for that session.
//
// Why this outlives the hook that fills it. The write goes out at the route id
// while the mirror is still hydrating, matches zero `offline_sessions` rows and
// comes back `RecordNotFound`. The recovery is to re-issue once the mirror
// resolves — but the clinician can switch consult or leave the page inside that
// same window, and a slot held on the hook instance dies there. The identifier
// then survives only in `offlinePatientLinksBySessionIdAtom`, which is display
// state: the field looks saved and the durable column is empty, which is the
// exact failure this feature exists to close.
//
// Keyed by session id rather than held per consumer because that is the real
// grain of the fact being recorded — "this session has an identifier that never
// reached its row" — and because two surfaces commit the same identifier by
// different gestures. A slot is only ever read back under the session it was
// filed under, so a route change abandons nothing and replays nothing onto a
// different encounter.
//
// A plain module map and not a jotai atom, deliberately: nothing renders off
// this. An atom read would wake both consumers on every commit and drain, and
// the recovery has to be readable from an effect that must not itself be a
// render input. Same shape as `pending-consent-replay-abort` beside it.

import { logger } from '@/utils/logger/logger';

export interface PendingOfflinePatientLinkWrite {
  /** The identifier the clinician typed, as it was committed. */
  patientLink: string;
  /** The id the refused write was addressed at, so a no-op re-issue is skipped. */
  attemptedId: string;
}

/**
 * How many sessions may hold an unlanded identifier at once.
 *
 * A bound rather than an unbounded map because these are patient identifiers
 * held in memory: a clinic day walking dozens of consults while the backend is
 * unreachable would otherwise accumulate every one of them for the life of the
 * process. Eight is comfortably more than the consults a clinician moves
 * between inside one hydration window, and small enough that the retained set
 * stays reviewable.
 */
export const PENDING_OFFLINE_PATIENT_LINK_WRITE_LIMIT = 8;

const pendingWrites = new Map<string, PendingOfflinePatientLinkWrite>();

/**
 * Record the identifier committed at an unresolved target for `sessionId`,
 * replacing any earlier one.
 *
 * Replacement is in commit order and synchronous, which is what makes two
 * commits before resolution safe: the later value wins and no out-of-order IPC
 * completion can resurrect the earlier one.
 */
export function recordPendingOfflinePatientLinkWrite(
  sessionId: string,
  write: PendingOfflinePatientLinkWrite
): void {
  if (!sessionId) return;
  // Deleted before it is set, so re-filing a session moves it to the tail.
  // `Map.set` on an existing key keeps the ORIGINAL insertion position, which
  // would let the bound below evict the identifier the clinician committed most
  // recently while an older one for another session survived.
  pendingWrites.delete(sessionId);
  pendingWrites.set(sessionId, write);
  const overflow = pendingWrites.size - PENDING_OFFLINE_PATIENT_LINK_WRITE_LIMIT;
  if (overflow <= 0) return;
  // A `Map` iterates in insertion order, so with the refresh above the head of
  // the key list is the least recently filed slot.
  for (const staleSessionId of [...pendingWrites.keys()].slice(0, overflow)) {
    pendingWrites.delete(staleSessionId);
  }
  // A dropped recovery slot is a dropped identifier, so it is reported rather
  // than evicted quietly. No id and no value: the key is a session id and the
  // value is PHI, and neither is needed to act on this.
  logger.warn('an unlanded offline patient-link write was evicted', {
    evicted: overflow,
    held: pendingWrites.size,
    limit: PENDING_OFFLINE_PATIENT_LINK_WRITE_LIMIT,
  });
}

/**
 * Read and remove the slot for `sessionId`.
 *
 * Read-and-remove in one call so a caller cannot re-issue twice off the same
 * slot: a second refusal at a resolved target is terminal, not a reason to
 * loop.
 */
export function takePendingOfflinePatientLinkWrite(
  sessionId: string
): PendingOfflinePatientLinkWrite | undefined {
  const pending = pendingWrites.get(sessionId);
  if (pending) pendingWrites.delete(sessionId);
  return pending;
}

/**
 * Drop the slot for `sessionId`, but only if it still holds exactly the write
 * that has now LANDED.
 *
 * The slot is filled synchronously when a write is issued at an unresolved
 * target, before anyone knows whether it will be accepted. That is what makes
 * the recovery immune to IPC ordering — but it also means a slot exists for
 * writes that then succeed, and a slot that outlives a successful write is a
 * re-issue of a value already in the row.
 *
 * Conditional, and compared by value, because a LATER commit may have replaced
 * the slot while this write was in flight. That newer identifier has not landed
 * and is the one the clinician means; clearing it here because an older write
 * finally succeeded would drop the recovery for the value that is actually
 * missing. Same reasoning as the commit-order replacement in
 * {@link recordPendingOfflinePatientLinkWrite}, read from the other end.
 */
export function forgetLandedOfflinePatientLinkWrite(
  sessionId: string,
  landed: PendingOfflinePatientLinkWrite
): void {
  const pending = pendingWrites.get(sessionId);
  if (!pending) return;
  if (pending.patientLink !== landed.patientLink) return;
  if (pending.attemptedId !== landed.attemptedId) return;
  pendingWrites.delete(sessionId);
}

/** Drop the slot for `sessionId` without reading it. */
export function forgetPendingOfflinePatientLinkWrite(sessionId: string): void {
  pendingWrites.delete(sessionId);
}

/**
 * Drop every held identifier. Called from `clearLogoutStorage`, and from tests
 * that need the module to start empty.
 *
 * In-memory is not the same as short-lived: neither sign-out path reloads the
 * webview, so without this these identifiers cross into the next account on a
 * shared clinical workstation — and the next mount of one of those session
 * routes would write the previous clinician's identifier under the new
 * account's token. Same exposure the patient-link drafts beside it are cleared
 * for.
 */
export function clearPendingOfflinePatientLinkWrites(): void {
  pendingWrites.clear();
}
